import React, { ReactElement } from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel, WintSuperkollAPIModelQualifiedShareDividend } from "wint-api-client/dist/types";
import { User } from "models/propTypes/userProfilePropTypes";
import { CommentItem } from "models/propTypes/commentViewPropTypes";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { QualifiedShareDividendProps } from "models/propTypes/qualifiedShareDividendsPropTypes";
import { KeyValue } from "models/utilTypes/keyValue";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { CurrencyEnums } from "models/enums";
//#endregion
//#region components
import {
  FileViewerContainer,
  TitledContainerInner,
  EntityCardContent,
  CommentsView,
  DetailsTableViewSmall,
  useDelayedEffect,
  useMountedState,
} from "components/common";
import QualifiedShareDividendTopCardContent from "./partials/topCardContent";
import QualifiedShareDividendFooter from "./footer";
import { useQualifiedShareDividendsService, useImageService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const QualifiedShareDividend: React.FC<QualifiedShareDividendProps> = (props) => {
  const qualifiedShareDividendsService = useQualifiedShareDividendsService();
  const imageService = useImageService();

  const { qualifiedShareDividend, reloadHandler } = props;
  const { getMounted } = useMountedState();

  const [attachments, setAttachments] = React.useState<ImageMeta[]>([]);
  const [loadingAttachments, setLoadingAttachments] = React.useState(true);

  const getDetailsList = () => {
    const details: Array<KeyValue<string, string | ReactElement> | typeof ACTION_SEPARATOR> = [
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.QualifiedShareDividends.Rule),
        qualifiedShareDividendsService.getRuleText(qualifiedShareDividend!.rule)
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.QualifiedShareDividends.DividendsToBeRaisedInCapital
        ),
        LocalizationHelperInstance.getFormattedCurrency(
          qualifiedShareDividend!.dividendToDeclareAsCapital,
          CurrencyEnums.CurrencyCode.SEK,
          0,
          0
        )
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.QualifiedShareDividends.DividendsToBeTaxedOnService
        ),
        LocalizationHelperInstance.getFormattedCurrency(
          qualifiedShareDividend!.dividendTaxedAsEmploymentIncome,
          CurrencyEnums.CurrencyCode.SEK,
          0,
          0
        )
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.QualifiedShareDividends.SavedDividendSpace),
        LocalizationHelperInstance.getFormattedCurrency(
          qualifiedShareDividend!.savedDividendScope,
          CurrencyEnums.CurrencyCode.SEK,
          0,
          0
        )
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.QualifiedShareDividends.DividendReceived),
        LocalizationHelperInstance.getFormattedCurrency(
          qualifiedShareDividend!.dividend,
          CurrencyEnums.CurrencyCode.SEK,
          0,
          0
        )
      ),
    ];

    return details;
  };

  function getComments() {
    return qualifiedShareDividend!.comments?.map(
      (message, index): CommentItem => ({
        id: index,
        message: message.comment ?? "",
        date: message.created,
        from: new User(message.profileId, message.profileName),
      })
    );
  }

  useDelayedEffect(
    () => {
      qualifiedShareDividend &&
        imageService
          .getImagesMetaByEntityIdAsGuid(
            qualifiedShareDividend.id ?? "",
            WintSuperkollAPIModel.ImageType.QualifiedShareDividend
          )
          .then((data) => {
            if (getMounted() && data.numberOfAttachments > 0)
              setAttachments(
                data.attachmentsMeta
                  ? data.attachmentsMeta.map((meta) => ({
                      id: meta.attachmentId ?? "",
                      contentType: meta.contentType,
                      numberOfPages: meta.numberOfPages,
                    }))
                  : []
              );
            setLoadingAttachments(false);
          });
    },
    100,
    [qualifiedShareDividend]
  );

  const fileViewer = React.useMemo(
    () =>
      attachments.length || loadingAttachments ? (
        <FileViewerContainer
          key={qualifiedShareDividend?.id}
          meta={attachments}
          parentId={qualifiedShareDividend?.id ?? 0}
          type={WintSuperkollAPIModel.ImageType.QualifiedShareDividend}
          loading={qualifiedShareDividend === undefined || loadingAttachments}
        />
      ) : undefined,
    [attachments, loadingAttachments, qualifiedShareDividend]
  );

  const getMainContainerHeader = () => {
    if (
      qualifiedShareDividend.state ===
      WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.AwaitingApproval
    )
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.QualifiedShareDividends.ToApproveHeader);
    else
      return LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.QualifiedShareDividends.SelectionHeaderText
      );
  };

  const getMainContainerSubHeader = () => {
    if (
      qualifiedShareDividend.state ===
      WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.AwaitingApproval
    )
      return LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.QualifiedShareDividends.ToApproveDescription
      );
    else
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.QualifiedShareDividends.SelectionSubText);
  };

  return (
    <EntityCardContent
      topTodoCard={
        <QualifiedShareDividendTopCardContent
          qualifiedShareDividend={qualifiedShareDividend}
          reloadHandler={reloadHandler}
        />
      }
      todoMainSection={
        <TitledContainerInner header={getMainContainerHeader()} subHeader={getMainContainerSubHeader()}>
          <DetailsTableViewSmall detailsList={getDetailsList()} hideButton />
        </TitledContainerInner>
      }
      footer={
        qualifiedShareDividend.id ? (
          <QualifiedShareDividendFooter
            qualifiedShareDividend={qualifiedShareDividend}
            markAsDoneHandler={props.reloadHandler}
            disableSuccessMessages
          />
        ) : (
          <></>
        )
      }
      infoViewer={fileViewer}
    >
      <CommentsView
        key={qualifiedShareDividend?.id}
        parentId={qualifiedShareDividend?.id ?? "0"}
        comments={getComments()}
        viewCountIncreaseButtonText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ShowMore)}
        viewCountIncreaseBy={10}
        defaultViewCount={2}
        totalComments={qualifiedShareDividend.comments?.length}
        defaultExpanded
      />
    </EntityCardContent>
  );
};
export default QualifiedShareDividend;
